<template>
  <div>
    <v-row class="d-flex" no-gutters>
      <v-col class="col-md-6 order-2 order-lg-0 col-lg-4 col-12 mb-2 pr-0 pr-md-2">
        <m-select
          data-cy="leadsOriginFilter"
          v-model="selectedType"
          :placeholder="$t('leads.list.filter-type')"
          :items="typeList"
          :showSelectedCounter="true"
          :multiple="true"
          :clearable="true">
          <template v-slot:prepend-inner>
            <i class="icon-mapit icon-filter-status"></i>
          </template>
        </m-select>
      </v-col>

      <v-col class="col-md-6 order-2 order-lg-0 col-lg-4 col-12 mb-2 pr-0 pr-md-2">
        <m-select
          data-cy="leadsStatusFilter"
          v-model="selectedStatus"
          :placeholder="$t('leads.list.filter-status')"
          :items="filterList"
          :showSelectedCounter="true"
          :multiple="true"
          :clearable="true">
          <template v-slot:prepend-inner>
            <i class="icon-mapit icon-filter-status"></i>
          </template>
        </m-select>
      </v-col>

      <v-col class="col-md-12 order-1 order-lg-0 col-lg-4 col-sm-12 mb-2">
        <m-text-field
          data-cy="leadsSearchFilter"
          v-model="search"
          :placeholder="$t('leads.list.search')"
          :clearable="true" />
      </v-col>
    </v-row>

    <v-data-table
      id="leads-table-list"
      :loading="isLoading"
      :loading-text="$t('leads.list.loading')"
      :headers="headers"
      :items="filteredLeadList"
      :custom-sort="customSort"
      :item-class="getRowClass"
      :items-per-page="10"
      class="elevation-0 pt-4"
      @click:row="onRowClick"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'fas fa-angle-double-left',
        lastIcon: 'fas fa-angle-double-right',
        prevIcon: 'fas fa-angle-left',
        nextIcon: 'fas fa-angle-right',
        'items-per-page-text': $t('leads.list.footer-num-x-page'),
        'items-per-page-options': [10, 25, 50, -1],
      }"
      :no-data-text="
        $t(leadItemsList && leadItemsList.length > 0 ? 'leads.list.no-data-search' : 'leads.list.no-data', {
          search,
        })
      "
      :no-results-text="$t('leads.list.no-data-search')">
      <template #footer.page-text="props">
        {{ props.pageStart }}-{{ props.pageStop }} {{ $t('honda.registration.list.of') }}
        {{ props.itemsLength }}
      </template>

      <template v-slot:item.createdAt="{item}">
        <span class="text-item">{{ formatDate(item.createdAt) }}</span>
      </template>

      <template v-slot:item.type="{item}">
        <label-tooltip max-width="150px" :is-tooltip-enabled="false">
          <span class="text-item">{{ getItemType(item) }}</span
          ><br />
        </label-tooltip>
      </template>

      <template v-slot:item.campaignName="{item}">
        <label-tooltip max-width="120px" :is-tooltip-enabled="false">
          <span class="text-item">{{ item.campaignName }}</span
          ><br />
        </label-tooltip>
      </template>

      <template v-slot:item.prospectName="{item}">
        <m-copy
          v-if="item.prospectName"
          :text-to-copy="item.prospectName"
          :show-client-history="Boolean(item.dealerContactId)"
          :origin="$route.path"
          @onCopy="onCopyClick"
          @onHistoryClick="handleHistoryClick(item.dealerContactId)">
          <span class="text-item">{{ item.prospectName }}</span>
        </m-copy>
      </template>

      <template v-slot:item.assignee="{item}">
        <lead-assignee-select
          :agent="item.assignee"
          :initial-agent="item.initialAgent"
          :dealer-users="dealerUsers"
          :is-dealer-admin="isDealerAdmin"
          :is-sales-agent="isSalesAgent"
          :user-email="userEmail"
          :status="item.status"
          @input="val => onAssigneeChange(item, val)" />
      </template>

      <template v-slot:item.status="{item}">
        <label-tooltip max-width="180px" :is-tooltip-enabled="false">
          <span :class="`text-item status status-${item.status.toLowerCase()}`">{{
            $t(`leads.status.${item.status}`)
          }}</span>
        </label-tooltip>
      </template>

      <template v-slot:item.offerId="{item}">
        <v-icon
          class="fa-solid fa-file"
          :color="`${item.offerId ? 'has-offer' : 'no-offer'}`"
          :key="`offer-${item.id}`" />
      </template>

      <template v-slot:item.followUp="{item}">
        <div v-if="item.followUp">
          <v-tooltip bottom color="black" :max-width="300">
            <template v-slot:activator="{on, attrs}">
              <span :class="`text-item ${isExpiredDate(item.followUp.dueDate)}`" v-bind="attrs" v-on="on">
                {{ formatDate(item.followUp.dueDate) }}
              </span>
            </template>
            <span style="hyphens: auto; overflow-wrap: break-word; white-space: pre-wrap">{{
              item.followUp.text
            }}</span>
          </v-tooltip>
        </div>
      </template>

      <template v-slot:item.actions="{item}">
        <leads-table-actions
          :item="item"
          :loading="loading"
          :mark-lead-as-read="markLeadAsRead"
          :mask-lead-as-lost="maskLeadAsLost" />
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, PropType, ref, watch} from '@vue/composition-api'
import {DateTime} from 'luxon'

import ridoApi from '@/api/rido.api'
import MCopy from '@/components/atom/MCopy.vue'
import LeadAssigneeSelect from '@/components/leads/LeadAssigneeSelect.vue'
import LeadsTableActions from '@/components/leads/LeadsTableActions.vue'
import LabelTooltip from '@/components/text/LabelTooltip.vue'
import {AgentAssignment, LeadStatus, LeadSummary, LogItem, OpportunityType} from '@/types'
import {isValidDate} from '@/utils/date-helper'
import {mapGetters} from '@/utils/map-store'

enum OpportunityTypeLegacy {
  Lease = 'LEASE',
  DemoBikeRequest = 'DEMO_BIKE_REQUEST',
}

enum LeadPriority {
  UnassignedNoFollowUp = 0,
  UnassignedWithFollowUp = 1,
  AssignedNoFollowUp = 2,
  AssignedWithFollowUp = 3,
  Other = 4,
}

export default defineComponent({
  name: 'LeadsTable',
  components: {MCopy, LabelTooltip, LeadsTableActions, LeadAssigneeSelect},
  props: {
    leadList: {
      type: Array as PropType<LeadSummary[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, {root, emit}) {
    const selectedStatus = ref<Array<string>>([LeadStatus.New, LeadStatus.InProgress])
    const selectedType = ref<Array<string>>([
      ...Object.values(OpportunityType),
      ...Object.values(OpportunityTypeLegacy),
    ])
    const search = ref<string | undefined>(undefined)
    const isLoading = ref(props.loading)
    const dealerUsers = ref<Array<{label: string; value: string}>>([])

    watch(
      () => props.loading,
      newValue => {
        isLoading.value = newValue
      },
    )

    onMounted(async () => {
      // Only fetch dealer users if user is dealer-admin
      // Sales-agents and other roles don't need the full dealer users list
      if (isDealerAdmin.value && getDealerId.value) {
        try {
          const response = await ridoApi.listDealerUsers(getDealerId.value)
          if (response.data && response.data.data) {
            // Filter to only include dealer-admin and sales-agent roles
            dealerUsers.value = response.data.data
              .filter((user: any) => user.role === 'dealer-admin' || user.role === 'sales-agent')
              .map((user: any) => ({
                label: user.name,
                value: user.email,
              }))
          }
        } catch (error) {
          console.error('Error fetching dealer users', error)
        }
      }
    })

    const {
      getLocale: locale,
      isDealerAdmin,
      isSalesAgent,
      getCognitoUser,
      getDealerId,
    } = mapGetters(root.$store.getters)

    const userEmail = computed(() => {
      const user = getCognitoUser.value
      return user?.attributes?.email || ''
    })

    const formatDate = (date: string) => {
      return DateTime.fromISO(date).toFormat('dd/MM/yyyy')
    }

    const filteredLeadList = computed(() => {
      const filtered = props.leadList
        .filter(item => (selectedStatus.value.length > 0 ? selectedStatus.value.includes(item.status || '') : true))
        .filter(item => (selectedType.value.length > 0 ? selectedType.value.includes(item.type || '') : true))
        .filter(item =>
          search.value && search.value !== ''
            ? item.prospectName?.toLowerCase().includes(search.value?.toLowerCase()) ||
              root.$t(`leads.types.${item.type}`).toString().toLowerCase().includes(search.value?.toLowerCase()) ||
              (item.assignee?.agent || item.assignee?.agentEmail || '')
                ?.toLowerCase()
                .includes(search.value?.toLowerCase())
            : true,
        )

      return filtered.sort(defaultLeadSort)
    })

    function customSort(items: LeadSummary[], sortBy: string[], sortDesc: boolean[]) {
      return [...items].sort((leadA, leadB) => {
        // Primary: active column sort (when user clicks a column header it takes precedence)
        if (sortBy.length > 0) {
          for (let i = 0; i < sortBy.length; i++) {
            const key = sortBy[i]
            const desc = sortDesc[i]
            const header = headers.find(h => h.value === key)

            let comparison = 0
            if (header?.sort) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              comparison = header.sort((leadA as any)[key], (leadB as any)[key])
            } else {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const leadAValue = String((leadA as any)[key] ?? '')
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const leadBValue = String((leadB as any)[key] ?? '')
              comparison = leadAValue.localeCompare(leadBValue)
            }

            if (comparison !== 0) {
              return desc ? -comparison : comparison
            }
          }
        }

        // Secondary: default ordering (priority weight, then follow-up date) as tie-breaker
        return defaultLeadSort(leadA, leadB)
      })
    }

    const typeList = computed(() => {
      return [...Object.values(OpportunityType), ...Object.values(OpportunityTypeLegacy)].map(type => ({
        text: root.$t(`leads.types.${type}`).toString(),
        value: type,
      }))
    })

    const filterList = computed(() => {
      return Object.values(LeadStatus).map(status => ({
        text: root.$t(`leads.status.${status}`).toString(),
        value: status,
      }))
    })

    const headers = [
      {
        text: root.$t('leads.list.createdAt'),
        align: 'left',
        value: 'createdAt',
        class: 'text-header',
        sortable: true,
        sort: (a: string, b: string) => (sameDay(a, b) ? compareDates(a, b) : b?.localeCompare(a)),
      },
      {
        text: root.$t('leads.list.type'),
        align: 'left',
        value: 'type',
        class: 'text-header',
        sortable: true,
      },
      {
        text: root.$t('leads.list.campaign'),
        align: 'left',
        value: 'campaignName',
        class: 'text-header',
        sortable: true,
      },
      {
        text: root.$t('leads.list.client'),
        align: 'left',
        value: 'prospectName',
        class: 'text-header',
        sortable: true,
      },
      {
        text: root.$t('leads.list.assigned-agent'),
        align: 'left',
        value: 'assignee',
        class: 'text-header',
        sortable: true,
        sort: (a?: AgentAssignment, b?: AgentAssignment) => {
          const aValue = a?.agent || a?.agentEmail || ''
          const bValue = b?.agent || b?.agentEmail || ''
          return aValue.localeCompare(bValue)
        },
      },
      {
        text: root.$t('leads.list.status'),
        align: 'left',
        value: 'status',
        class: 'text-header',
        sortable: true,
        sort: (a: LeadStatus, b: LeadStatus) => statusWeight(a) - statusWeight(b),
      },
      {
        text: root.$t('leads.list.offer'),
        align: 'center',
        value: 'offerId',
        class: 'text-header',
        sortable: true,
        width: '148px',
        sort: (a: string, b: string) => (a ? 0 : 1) - (b ? 0 : 1),
      },
      {
        text: root.$t('leads.list.follow-up'),
        align: 'center',
        value: 'followUp',
        class: 'text-header',
        sortable: true,
        sort: sortFollowUps,
      },
      {
        text: root.$t('leads.list.action'),
        align: 'center',
        value: 'actions',
        class: 'text-header',
        width: '5%',
        sortable: false,
      },
    ]

    function sameDay(a: string, b: string) {
      return a?.localeCompare(b) === 0
    }

    function compareDates(a: string, b: string) {
      return DateTime.fromISO(a) < DateTime.fromISO(b) ? 1 : -1
    }

    function sortFollowUps(a?: LogItem, b?: LogItem) {
      if (!a?.dueDate && !b?.dueDate) return 0
      if (!a?.dueDate) return 1
      if (!b?.dueDate) return -1

      const aDate = DateTime.fromISO(a.dueDate)
      const bDate = DateTime.fromISO(b.dueDate)

      if (aDate < bDate) return -1
      if (aDate > bDate) return 1
      return 0
    }

    function getLeadFollowUp(lead: LeadSummary): LogItem | undefined {
      return lead.followUp || lead.commentLog?.[0]
    }

    function defaultFollowUpSort(a: LeadSummary, b: LeadSummary) {
      return sortFollowUps(getLeadFollowUp(a), getLeadFollowUp(b))
    }

    // Default ordering used both when no column is selected and as a tie-breaker:
    // group by priority weight first, then by follow-up date.
    function defaultLeadSort(a: LeadSummary, b: LeadSummary) {
      const aPriority = getPriorityWeight(a)
      const bPriority = getPriorityWeight(b)

      if (aPriority !== bPriority) {
        return aPriority - bPriority
      }

      return defaultFollowUpSort(a, b)
    }

    function getPriorityWeight(lead: LeadSummary): LeadPriority {
      const isNew = lead.status === LeadStatus.New
      const isAssigned = Boolean(lead.assignee?.agentEmail || lead.assignee?.agent)
      const hasFollowUp = Boolean(getLeadFollowUp(lead))

      if (isNew && !isAssigned && !hasFollowUp) {
        return LeadPriority.UnassignedNoFollowUp
      }
      if (isNew && !isAssigned && hasFollowUp) {
        return LeadPriority.UnassignedWithFollowUp
      }
      if (isNew && isAssigned && !hasFollowUp) {
        return LeadPriority.AssignedNoFollowUp
      }
      if (isNew && isAssigned && hasFollowUp) {
        return LeadPriority.AssignedWithFollowUp
      }
      return LeadPriority.Other
    }

    function isExpiredDate(date: string) {
      return isValidDate(date) && DateTime.fromISO(date) < DateTime.local().startOf('day') ? 'expired' : ''
    }

    function statusWeight(a: LeadStatus) {
      switch (a) {
        case LeadStatus.New:
          return -1
        case LeadStatus.InProgress:
          return 0
        case LeadStatus.WithOffer:
          return 1
        case LeadStatus.Lost:
          return 2
        default:
          return 3
      }
    }

    function viewLead(id: string) {
      root.$router.push({name: 'LeadDetail', params: {id}})
    }

    function getRowClass(row: LeadSummary) {
      return row.read ? 'click lead-item' : 'click lead-item__unread'
    }

    function onRowClick(row: LeadSummary) {
      viewLead(row.id)
    }

    function onCopyClick() {
      root.$store.dispatch('showAlertSuccess', 'leads.list.copy-success')
    }

    function handleHistoryClick(id: string) {
      root.$router.push({name: 'ClientHistory', params: {id}})
    }

    function getItemType(item: LeadSummary) {
      return root.$te(`leads.types.${item.type}`) ? root.$t(`leads.types.${item.type}`) : item.type
    }

    async function onAssigneeChange(item: LeadSummary, newAssignee: string) {
      const currentAssigneeEmail = item.assignee?.agentEmail || ''
      if (currentAssigneeEmail === newAssignee) return

      try {
        await ridoApi.assignLead(item.id, newAssignee)
        root.$store.dispatch('showAlertSuccess', 'leads.list.agent-assigned-success')
        emit('refresh-leads')
      } catch (error: any) {
        console.error('Failed to assign lead', error)
        const errorMessage = error?.response?.data?.error || error?.response?.data?.message
        if (errorMessage?.includes('inactive user')) {
          root.$store.dispatch('showAlertError', 'leads.list.inactive-user-error')
        } else {
          root.$store.dispatch('showAlertError', 'common.error')
        }
      }
    }

    return {
      compareDates,
      customSort,
      dealerUsers,
      filteredLeadList,
      filterList,
      getItemType,
      getRowClass,
      headers,
      isExpiredDate,
      leadItemsList: props.leadList,
      LeadStatus,
      isLoading,
      onCopyClick,
      handleHistoryClick,
      onRowClick,
      sameDay,
      search,
      selectedStatus,
      selectedType,
      typeList,
      viewLead,
      formatDate,
      onAssigneeChange,
      isDealerAdmin,
      isSalesAgent,
      userEmail,
      markLeadAsRead: async (lead: LeadSummary) => {
        emit('mark-lead-as-read', lead)
        return Promise.resolve()
      },
      maskLeadAsLost: async (lead: LeadSummary) => {
        emit('mark-lead-as-lost', lead)
        return Promise.resolve()
      },
    }
  },
})
</script>

<style scoped lang="scss">
%status {
  content: '';
  width: 10px;
  min-width: 10px;
  height: 10px;
  margin-right: 10px;
  border-radius: 100%;
}

.status {
  display: flex;
  align-items: center;

  &-new:before {
    @extend %status;
    background: #fec478;
  }

  &-in_progress:before {
    @extend %status;
    background: #49bcf4;
  }

  &-with_offer:before {
    @extend %status;
    background: #48a44a;
  }

  &-lost:before {
    @extend %status;
    background: #bdbdbd;
  }
}

.list-menu-actions {
  min-width: 200px !important;

  .v-list-item {
    &:hover {
      background-color: #f5f5f5;
    }

    &__title {
      font-size: 14px;
    }
  }

  .v-list-item__title {
    font-size: 14px;
  }
}
</style>

<style lang="scss">
.has-offer--text {
  color: #00803e !important;
  font-size: 20px !important;
}

.no-offer--text {
  color: #dddddd !important;
  font-size: 20px !important;
}

.lead-item {
  height: 60px;

  &__unread {
    @extend .lead-item;
    background: #ecf3ff;
  }
}

.cursor-normal {
  cursor: default;
}

.lead-copy {
  cursor: pointer;
  margin-left: 10px;

  i {
    font-size: 16px !important;
  }
}

.text-item.expired {
  color: #dc182d;
}

#leads-table-list .m-button-select {
  max-width: 200px;
  width: fit-content;

  .m-button-select__trigger {
    max-width: 100%;
  }
}
</style>
